import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Check } from "lucide-react";

export default function Contact() {
  const [params] = useSearchParams();
  const isSponsor = params.get("tipo") === "sponsor";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState(
    isSponsor ? "Salve Fabio, sono interessato/a a uno spazio pubblicitario sul blog." : ""
  );
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error("Compila nome, email e messaggio");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", {
        name,
        email,
        company,
        message,
        topic: isSponsor ? "Spazio pubblicitario" : "Generale",
      });
      setSent(true);
      toast.success("Messaggio inviato. Grazie!");
    } catch {
      toast.error("Invio non riuscito, riprova");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="relative z-10">
      <Header />

      <section className="max-w-2xl mx-auto px-6 md:px-8 pt-16 md:pt-24 pb-10">
        <p className="label-ui text-[#9F583F] mb-5 fade-up">
          {isSponsor ? "Spazi pubblicitari" : "Scrivimi"}
        </p>
        <h1 className="font-display text-5xl md:text-6xl leading-[0.95] tracking-tight text-[#1C1917] fade-up">
          {isSponsor ? (
            <>Porta la tua <span className="italic text-[#9F583F]">azienda</span> qui.</>
          ) : (
            <>Mettiamoci in <span className="italic text-[#9F583F]">contatto</span>.</>
          )}
        </h1>
        <p className="font-body text-lg text-[#57534E] mt-6 leading-relaxed fade-up">
          {isSponsor
            ? "Raccontami della tua attività: ti risponderò con le informazioni per riservare uno spazio nel blog."
            : "Hai un'idea, una domanda o una proposta? Compila il modulo, ti risponderò appena possibile."}
        </p>

        {sent ? (
          <div className="mt-12 border border-[#E7E5E4] bg-[#F2F0EB] p-10 text-center" data-testid="contact-success">
            <div className="w-14 h-14 rounded-full bg-[#9F583F] text-white flex items-center justify-center mx-auto mb-5">
              <Check size={26} strokeWidth={2} />
            </div>
            <h2 className="font-display text-3xl text-[#1C1917]">Messaggio inviato!</h2>
            <p className="font-body text-[#57534E] mt-3">
              Grazie per avermi scritto. Ti risponderò al più presto.
            </p>
            <Link to="/" className="inline-block mt-8 label-ui text-[#9F583F] link-underline">
              Torna agli articoli
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-12" data-testid="contact-form">
            <label className="label-ui text-[#57534E] block mb-2">Nome</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="contact-name-input"
              className="font-body text-lg rounded-none border-x-0 border-t-0 border-b border-[#E7E5E4] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#9F583F] mb-7"
            />
            <label className="label-ui text-[#57534E] block mb-2">Email</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="contact-email-input"
              className="font-body text-lg rounded-none border-x-0 border-t-0 border-b border-[#E7E5E4] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#9F583F] mb-7"
            />
            <label className="label-ui text-[#57534E] block mb-2">
              Azienda {isSponsor ? "" : "(facoltativo)"}
            </label>
            <Input
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              data-testid="contact-company-input"
              className="font-body text-lg rounded-none border-x-0 border-t-0 border-b border-[#E7E5E4] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#9F583F] mb-7"
            />
            <label className="label-ui text-[#57534E] block mb-2">Messaggio</label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              data-testid="contact-message-input"
              className="font-body text-lg leading-relaxed rounded-none border border-[#E7E5E4] bg-transparent focus-visible:ring-0 focus-visible:border-[#9F583F] resize-y"
            />
            <Button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="mt-7 rounded-none bg-[#1C1917] hover:bg-[#9F583F] text-white label-ui px-8 py-6 h-auto transition-colors"
            >
              {sending ? "Invio…" : "Invia messaggio"}
            </Button>
          </form>
        )}
      </section>

      <Footer />
    </div>
  );
}
